/**
 * Hover effect for links and buttons, after fancycomponents.dev text effects:
 * - ScrambleHover (useOriginalCharsOnly, sequential, revealDirection 'start'): the label's own
 *   characters are shuffled, then settle into place from first to last.
 * - A light take on Letter3DSwap: each letter tips back 20° as it settles.
 *
 * Only text nodes are touched, so icons and nested markup stay intact. The original nodes are put
 * back once the animation ends. Opt out on any element with `data-no-scramble`.
 * On cards, hovering anywhere on the card plays the effect on its title or "Read more" link.
 */

const TARGETS = 'a[href], button, .button, .button-secondary, [role="button"]';
const SKIP = '[data-no-scramble], svg, script, style, .visually-hidden, [hidden], [data-scramble-word]';
// Cards: entering anywhere on the card plays the effect on one element of it
// (product title; the "Read more" link on blog cards, whose titles opt out with data-no-scramble).
const CARDS = 'product-card, product-card-link, [data-scramble-card], slideshow-slide:has([data-scramble-card-target])';
const CARD_TARGETS = '[ref="productTitleLink"], .resource-card__title, [data-scramble-card-target]';
const MAX_LENGTH = 60; // longer labels (cards, paragraphs) are left alone
const SCRAMBLE_SPEED = 35; // ms per settled character (mirrors --scramble-stagger)
const DURATION = 300; // ms of one letter tilt (mirrors --scramble-duration)

const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/** @type {WeakSet<Element>} */
const running = new WeakSet();

/**
 * @param {string[]} array
 */
function shuffle(array) {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

/**
 * @param {Element} element
 * @returns {Text[]}
 */
function getTextNodes(element) {
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (!node.data.trim()) return NodeFilter.FILTER_REJECT;
      if (node.parentElement?.closest(SKIP)) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });

  /** @type {Text[]} */
  const nodes = [];
  while (walker.nextNode()) nodes.push(/** @type {Text} */ (walker.currentNode));
  return nodes;
}

/**
 * @param {HTMLElement} element
 */
function animate(element) {
  const nodes = getTextNodes(element);
  const text = nodes.map((node) => node.data).join('');
  if (!text.trim() || text.trim().length > MAX_LENGTH) return;

  running.add(element);

  const addedLabel = !element.hasAttribute('aria-label');
  if (addedLabel) element.setAttribute('aria-label', text.replace(/\s+/g, ' ').trim());

  /** @type {{ char: string, face: HTMLElement }[]} */
  const letters = [];
  /** @type {[HTMLElement, Text][]} */
  const swaps = [];

  for (const node of nodes) {
    const word = document.createElement('span');
    word.dataset.scrambleWord = '';
    word.setAttribute('aria-hidden', 'true');

    for (const char of node.data) {
      if (char.trim() === '') {
        word.append(char);
        continue;
      }

      const letter = document.createElement('span');
      letter.className = 'scramble-letter';
      letter.style.setProperty('--i', String(letters.length));

      // The hidden original keeps each letter's width, so shuffled glyphs don't shift the layout.
      const ghost = document.createElement('span');
      ghost.className = 'scramble-letter__ghost';
      ghost.textContent = char;

      const face = document.createElement('span');
      face.className = 'scramble-letter__face';

      letter.append(ghost, face);
      word.append(letter);
      letters.push({ char, face });
    }

    swaps.push([word, node]);
    node.replaceWith(word);
  }

  let revealed = 0;

  function render() {
    // Letters not settled yet show the remaining original characters, shuffled among themselves.
    const pool = shuffle(letters.slice(revealed).map((letter) => letter.char));
    letters.forEach((letter, index) => {
      letter.face.textContent = index < revealed ? letter.char : pool.pop() ?? letter.char;
    });
  }

  render();

  const interval = window.setInterval(() => {
    revealed++;
    render();
    if (revealed >= letters.length) window.clearInterval(interval);
  }, SCRAMBLE_SPEED);

  const total = (letters.length - 1) * SCRAMBLE_SPEED + DURATION + 60;

  window.setTimeout(() => {
    window.clearInterval(interval);
    // Put the real text nodes back. A detached word (section re-rendered meanwhile) is a no-op.
    for (const [word, node] of swaps) word.replaceWith(node);
    if (addedLabel) element.removeAttribute('aria-label');
    running.delete(element);
  }, total);
}

/**
 * @param {PointerEvent} event
 */
function onPointerOver(event) {
  if (!canHover.matches || reducedMotion.matches || event.pointerType === 'touch') return;
  if (!(event.target instanceof Element)) return;

  /**
   * True when the pointer only moved between children of `container`.
   * @param {Element} container
   */
  const movedWithin = (container) =>
    event.relatedTarget instanceof Node && container.contains(event.relatedTarget);

  const card = event.target.closest(CARDS);
  if (card && !card.closest('[data-no-scramble]') && !movedWithin(card)) {
    const target = /** @type {HTMLElement | null} */ (card.querySelector(CARD_TARGETS));
    if (target && !running.has(target)) animate(target);
  }

  const element = /** @type {HTMLElement | null} */ (event.target.closest(TARGETS));
  if (!element || element.closest('[data-no-scramble]') || movedWithin(element)) return;
  // Inside a card the target is driven by the card itself, not by hovering it directly.
  if (card && element.matches(CARD_TARGETS)) return;

  if (!running.has(element)) animate(element);
}

document.addEventListener('pointerover', onPointerOver, { passive: true });
